import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import Insight from "../data/insight/insightClass";
import { fetchInsightsPage } from "../api/insights";
import { insights as staticInsights, weekly as staticWeekly, events as staticEvents } from "../data/insight/insightsExport";

const PREVIEW_SIZE = 12;

interface InsightsContextValue {
  articles: Insight[];
  weekly: Insight[];
  events: Insight[];
  relatedPool: Insight[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const InsightsContext = createContext<InsightsContextValue | null>(null);

function staticFallback() {
  return {
    articles: staticInsights,
    weekly: staticWeekly,
    events: staticEvents,
  };
}

export function InsightsProvider({ children }: { children: React.ReactNode }) {
  const [articles, setArticles] = useState<Insight[]>([]);
  const [weekly, setWeekly] = useState<Insight[]>([]);
  const [events, setEvents] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [articlesPage, weeklyPage, eventsPage] = await Promise.all([
        fetchInsightsPage("articles", 1, PREVIEW_SIZE),
        fetchInsightsPage("weekly", 1, PREVIEW_SIZE),
        fetchInsightsPage("events", 1, PREVIEW_SIZE),
      ]);

      const total = articlesPage.total + weeklyPage.total + eventsPage.total;
      if (total === 0) {
        const fallback = staticFallback();
        setArticles(fallback.articles);
        setWeekly(fallback.weekly);
        setEvents(fallback.events);
      } else {
        setArticles(articlesPage.items.map(Insight.fromApi));
        setWeekly(weeklyPage.items.map(Insight.fromApi));
        setEvents(eventsPage.items.map(Insight.fromApi));
      }
    } catch {
      const fallback = staticFallback();
      setArticles(fallback.articles);
      setWeekly(fallback.weekly);
      setEvents(fallback.events);
      setError("Using cached static insights — API unavailable");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const relatedPool = [...articles, ...weekly, ...events];

  return (
    <InsightsContext.Provider
      value={{ articles, weekly, events, relatedPool, loading, error, refresh: load }}
    >
      {children}
    </InsightsContext.Provider>
  );
}

export function useInsights() {
  const ctx = useContext(InsightsContext);
  if (!ctx) throw new Error("useInsights must be used within InsightsProvider");
  return ctx;
}
